import React, { useState } from 'react';
import { Button, Input, Textarea } from '@heroui/react';
import { Drawer, DrawerContent, DrawerHeader, DrawerBody, DrawerFooter } from '@heroui/drawer';
import { sendOrderEmail } from '../apiEndpoints';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import CloseIcon from './CloseIcon';
import useIsMobile from '../hooks/useIsMobile';
import { btnAccent, btnGhost, label as labelCls } from '@/components/ui/design';

const fieldWrap = 'rounded-2xl bg-grey99 shadow-none ring-1 ring-black/[0.04] data-[hover=true]:bg-grey95 group-data-[focus=true]:bg-grey99 group-data-[focus=true]:ring-2 group-data-[focus=true]:ring-primary/50 dark:bg-white/[0.06] dark:ring-white/10 dark:data-[hover=true]:bg-white/10 dark:group-data-[focus=true]:bg-white/[0.06] dark:group-data-[focus=true]:ring-nightAccent/60';
const fieldText = 'text-base text-grey10 placeholder:text-grey60 dark:text-white dark:placeholder:text-nightMuted';

interface EmailModalProps {
    isOpen: boolean;
    onClose: () => void;
    order: any;
    eventTitle?: string;
}

export default function EmailModal({ isOpen, onClose, order, eventTitle }: EmailModalProps) {
    const [subject, setSubject] = useState('');
    const [message, setMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const queryClient = useQueryClient();
    const isMobile = useIsMobile();
    const sendEmailMutation = useMutation({
        mutationFn: (data: { subject: string; body: string }) =>
            sendOrderEmail(order._id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['eventOrders', order.eventId] });
            handleClose();
        },
        onError: (error) => {
            console.error('Failed to send email:', error);
            setIsLoading(false);
        }
    });

    const handleClose = () => {
        setSubject('');
        setMessage('');
        setIsLoading(false);
        onClose();
    };

    const handleSendEmail = async () => {
        if (!subject.trim() || !message.trim()) {
            return;
        }

        setIsLoading(true);
        sendEmailMutation.mutate({ subject: subject.trim(), body: message.trim() });
    };

    const customerName = order.customerName || order.guestName || order.attendeeName || 'Customer';
    const customerEmail = order.customerEmail || order.guestEmail || order.email || 'N/A';

    return (
        <Drawer
            placement="right"
            hideCloseButton
            isOpen={isOpen}
            onClose={handleClose}
            size={isMobile ? 'full' : 'lg'}
            classNames={{
                wrapper: isMobile ? 'z-[100000]' : 'top-[80px] h-[calc(100dvh-80px)] z-[100000]',
                base: isMobile ? 'bg-white dark:bg-nightSurface h-[100dvh] my-0 z-[100000]' : 'bg-white dark:bg-nightSurface h-[calc(100dvh-80px)] my-0 z-[100000] rounded-l-[28px] ring-1 ring-black/[0.06] dark:ring-white/10',
                backdrop: isMobile ? 'z-[100000] bg-black/50' : 'top-[80px] h-[calc(100dvh-80px)] z-[100000] bg-black/50',
                header: "pt-6 pb-4",
                body: "py-4 flex-1",
                footer: "pt-4 pb-6"
            }}
            scrollBehavior="inside"
            isDismissable={true}
                    >
            <DrawerContent>
                <DrawerHeader className="flex items-start justify-between px-4 md:px-6">
                    <div className="flex-1 min-w-0">
                        <h3 className="text-[28px] font-bold leading-[1] tracking-[-0.04em] text-grey10 dark:text-white">Send Email to Customer</h3>
                        <p className="text-sm text-grey40 dark:text-nightMuted mt-2 break-words">
                            Send an email to {customerName} ({customerEmail})
                        </p>
                    </div>

                    <button
                        type="button"
                        onClick={handleClose}
                        className="w-10 h-10 flex items-center justify-center rounded-full ring-1 ring-grey90 dark:ring-white/15 text-grey10 dark:text-white hover:bg-grey99 dark:hover:bg-white/[0.06] transition-colors flex-shrink-0"
                        aria-label="Close"
                    >
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                            <path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                        </svg>
                    </button>
                </DrawerHeader>

                <DrawerBody className="px-4 md:px-6">
                    <div className="space-y-4">
                        {/* Order Info */}
                        <div className="rounded-[28px] bg-grey99 p-5 ring-1 ring-black/[0.04] dark:bg-white/[0.04] dark:ring-white/[0.06]">
                            <h4 className="text-xs font-bold uppercase tracking-[0.18em] text-grey40 dark:text-nightMuted mb-3">Order Details</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4 text-sm">
                                <div className="flex flex-col">
                                    <span className="text-grey40 dark:text-nightMuted text-xs">Order ID:</span>
                                    <span className="text-grey10 dark:text-white font-semibold break-all">{order.hashedUniqueId || order.orderId}</span>
                                </div>
                                <div className="flex flex-col">
                                    <span className="text-grey40 dark:text-nightMuted text-xs">Event:</span>
                                    <span className="text-grey10 dark:text-white font-semibold break-words">
                                        {eventTitle || (order.eventTitle || order.event?.title || 'N/A')}
                                    </span>
                                </div>
                                <div className="flex flex-col">
                                    <span className="text-grey40 dark:text-nightMuted text-xs">Customer:</span>
                                    <span className="text-grey10 dark:text-white font-semibold">{customerName}</span>
                                </div>
                                <div className="flex flex-col">
                                    <span className="text-grey40 dark:text-nightMuted text-xs">Email:</span>
                                    <span className="text-grey10 dark:text-white font-semibold break-all">{customerEmail}</span>
                                </div>
                            </div>
                        </div>

                        {/* Email Form */}
                        <div className="space-y-4">
                            <div>
                                <label className={labelCls}>
                                    Subject *
                                </label>
                                <Input
                                    value={subject}
                                    onChange={(e) => setSubject(e.target.value)}
                                    placeholder="Enter email subject..."
                                    className="w-full"
                                    variant="flat"
                                    size="lg"
                                    classNames={{ inputWrapper: `h-14 ${fieldWrap}`, input: fieldText }}
                                />
                            </div>

                            <div>
                                <label className={labelCls}>
                                    Message *
                                </label>
                                <Textarea
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                    placeholder="Enter your message..."
                                    className="w-full"
                                    variant="flat"
                                    size="lg"
                                    minRows={4}
                                    maxRows={8}
                                    classNames={{ inputWrapper: fieldWrap, input: fieldText }}
                                />
                            </div>
                        </div>
                    </div>
                </DrawerBody>

                <DrawerFooter className="flex flex-col sm:flex-row gap-3 px-4 md:px-6">
                    <Button
                        variant="light"
                        onPress={handleClose}
                        className={`${btnGhost} w-full sm:w-auto bg-transparent`}
                        isDisabled={isLoading}
                    >
                        Cancel
                    </Button>
                    <Button
                        color="primary"
                        onPress={handleSendEmail}
                        isLoading={isLoading}
                        isDisabled={!subject.trim() || !message.trim()}
                        className={`${btnAccent} w-full sm:w-auto`}
                    >
                        Send Email
                    </Button>
                </DrawerFooter>
            </DrawerContent>
        </Drawer>
    );
}
